<!-- SettlementSection.vue -->
<template>
  <aside
      class="settlement-section bg-gray-800/70 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-700 overflow-hidden w-full max-w-full min-w-0"
      dir="rtl"
  >
    <header class="px-2.5 py-2 bg-gray-900/50 border-b border-gray-700">
      <div class="flex items-start justify-between gap-3 min-w-0">
        <div class="min-w-0">
          <h3 class="text-sm font-bold text-gray-300 truncate">
            {{ title }}
          </h3>

          <p class="text-[11px] text-gray-400 mt-1 leading-5">
            {{ subtitle }}
          </p>

          <p class="text-[10px] text-amber-300 mt-1 leading-5">
            موجودی فقط بعد از تأیید شریک کم می‌شود.
          </p>
        </div>

        <div class="flex items-center gap-2 shrink-0">
          <button
              @click="$router.push({ name: 'PartnerStockExitManager' })"
              class="px-2.5 py-2 rounded-xl bg-blue-600/20 border border-blue-500/30 text-blue-200 hover:bg-blue-600/30 transition text-xs"
              title="مشاهده همه"
          >
            <i class="fas fa-eye"></i>
          </button>

          <button
              @click="$router.push({ name: 'PartnerStockExitCreate' })"
              class="px-2.5 py-2 rounded-xl bg-emerald-600/20 border border-emerald-500/30 text-emerald-200 hover:bg-emerald-600/30 transition text-xs"
              title="ثبت خروج"
          >
            <i class="fas fa-plus"></i>
          </button>
        </div>
      </div>
    </header>
    <div class="p-4 space-y-4">
      <div
          v-if="notice"
          class="rounded-2xl border border-emerald-500/25 bg-emerald-600/10 px-3 py-2 text-xs text-emerald-200"
      >
        {{ notice }}
      </div>
      <div v-if="error" class="py-8 text-center">
        <i class="fas fa-exclamation-triangle text-red-400 text-xl"></i>
        <p class="text-xs text-red-300 mt-3">
          {{ error }}
        </p>

        <button
            @click="fetchExits"
            class="mt-4 px-3 py-2 rounded-xl bg-red-600/20 border border-red-500/30 text-red-200 text-xs hover:bg-red-600/30 transition"
        >
          تلاش مجدد
        </button>
      </div>

      <template v-else>

        <div class="rounded-3xl border border-white/10 bg-slate-900/80 overflow-hidden shadow-2xl">
          <div class="overflow-auto">
            <table class="w-full text-xs">
              <thead class="bg-slate-950 text-slate-300 sticky top-0">
              <tr>
                <th class="px-2.5 py-2 text-center">شناسه</th>
                <th class="px-2.5 py-2 text-center">تاریخ</th>
                <th class="px-2.5 py-2 text-center">تعداد ردیف</th>
                <th class="px-2.5 py-2 text-center">جمع تعداد</th>
                <th class="px-2.5 py-2 text-center">وضعیت</th>
                <th class="px-2.5 py-2 text-center">عملیات</th>
              </tr>
              </thead>

              <tbody class="divide-y divide-white/5">
              <tr
                  v-for="x in exits.items"
                  :key="x.id"
                  class="hover:bg-slate-800/40 transition"
              >
                <td class="px-2.5 py-2 text-center text-cyan-300 font-bold">
                  #{{ x.id }}
                </td>


                <td class="px-2.5 py-2 text-center text-slate-300 whitespace-nowrap">
                  {{ x.exit_date || x.created_at || '---' }}
                </td>

                <td class="px-2.5 py-2 text-slate-300 text-center">
                  {{ (x.items || []).length }}
                </td>

                <td class="px-2.5 py-2 text-slate-300 text-center text-emerald-300 font-bold">
                  {{ x.total_quantity || calcExitTotal(x) }}
                </td>

                <td class="px-2.5 py-2  text-center">
                  <span
                      class="px-2 py-1 rounded-lg border text-[9px]"
                      :class="statusClass(x.status)"
                  >
                    {{ statusLabel(x.status) }}
                  </span>
                </td>
                <td class="px-2.5 py-2 text-center">
                  <div class="flex flex-wrap items-center justify-center gap-2">
                    <button
                        @click="openDetails(x)"
                        class="px-2 py-1 rounded-lg bg-slate-700/70 border border-slate-600 text-[11px] text-slate-100 hover:bg-slate-600 transition"
                    >
                      جزئیات
                    </button>

                    <button
                        @click="printExit(x)"
                        class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-cyan-600/10 border border-cyan-500/25 text-[11px] text-cyan-200 hover:bg-cyan-600/20 transition"
                        title="چاپ رسید خروج"
                    >
                      <i class="fas fa-print text-[10px]"></i>
                      رسید
                    </button>

                    <button
                        v-if="canConfirmExit(x)"
                        @click="openConfirmModal(x, 'confirm')"
                        class="px-2 py-1 rounded-lg bg-emerald-600/30 border border-emerald-500/30 text-[14px] text-emerald-200 hover:bg-emerald-600/25 transition"
                    >
                      تأیید
                    </button>

                    <button
                        v-if="canCancelExit(x)"
                        @click="openConfirmModal(x, 'cancel')"
                        class="px-2 py-1 rounded-lg bg-rose-600/30 border border-rose-500/30 text-[14px] text-rose-200 hover:bg-rose-600/25 transition"
                    >
                      لغو
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="!exits.items.length && !loading.list">
                <td colspan="8" class="px-6 py-16 text-center text-slate-500">
                  درخواستی پیدا نشد.
                </td>
              </tr>

              <tr v-if="loading.list">
                <td colspan="8" class="px-6 py-16 text-center text-slate-400">
                  در حال دریافت اطلاعات...
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
    <!-- Details Modal -->
    <div
        v-if="detailsModal.visible"
        class="fixed inset-0 z-[99990] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        @click.self="closeDetails"
    >
      <div class="w-full max-w-2xl max-h-[90vh] overflow-auto rounded-3xl border border-white/10 bg-slate-950 shadow-2xl">
        <header class="sticky top-0 bg-slate-950/95 border-b border-white/10 px-2.5 py-2 flex items-center justify-between gap-3">
          <div>
            <h4 class="text-sm font-bold text-white">
              جزئیات خروج دستی #{{ detailsModal.item.id }}
            </h4>
            <p class="text-[11px] text-slate-400 mt-1">
              {{ statusLabel(detailsModal.item.status) }} / {{ formatDate(detailsModal.item.exit_date || detailsModal.item.created_at) }}
            </p>
          </div>

          <button
              @click="closeDetails"
              class="w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10"
          >
            ×
          </button>
        </header>

        <div class="p-4 space-y-4">
          <div class="grid grid-cols-2 gap-2">
            <div class="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
              <p class="text-[10px] text-slate-500">پارتنر</p>
              <p class="text-xs text-slate-100 mt-1">
                {{ partnerDisplay(detailsModal.item) }}
              </p>
            </div>

            <div class="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
              <p class="text-[10px] text-slate-500">جمع تعداد</p>
              <p class="text-xs text-emerald-300 font-bold mt-1" dir="ltr">
                {{ formatNumber(detailsModal.item.total_quantity || calcExitTotal(detailsModal.item)) }}
              </p>
            </div>
          </div>

          <div
              v-if="detailsModal.item.description"
              class="rounded-2xl border border-white/10 bg-white/[0.03] p-3"
          >
            <p class="text-[10px] text-slate-500">توضیحات</p>
            <p class="text-xs text-slate-200 mt-1 leading-6">
              {{ detailsModal.item.description }}
            </p>
          </div>

          <div class="rounded-2xl border border-white/10 overflow-hidden">
            <table class="w-full text-xs">
              <thead class="bg-slate-900 text-slate-400">
              <tr>
                <th class="px-3 py-2 text-right">محصول</th>
                <th class="px-3 py-2 text-center">کد</th>
                <th class="px-3 py-2 text-center">تعداد</th>
              </tr>
              </thead>

              <tbody class="divide-y divide-white/5">
              <tr
                  v-for="it in (detailsModal.item.items || [])"
                  :key="it.id"
              >
                <td class="px-3 py-2 text-slate-100">
                  {{ it.product_name || it.product || '-' }}
                  <div v-if="it.variation_of_name" class="text-[10px] text-slate-500 mt-1">
                    {{ it.variation_of_name }}
                  </div>
                </td>

                <td class="px-3 py-2 text-center text-slate-400" dir="ltr">
                  {{ it.product_sixteen_digit_code || '-' }}
                </td>

                <td class="px-3 py-2 text-center text-emerald-300 font-bold" dir="ltr">
                  {{ formatNumber(it.quantity) }}
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <div class="flex flex-wrap items-center justify-end gap-2">
            <button
                @click="printExit(detailsModal.item)"
                class="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-cyan-600/10 border border-cyan-500/25 text-xs text-cyan-200 hover:bg-cyan-600/20 transition"
            >
              <i class="fas fa-print text-[11px]"></i>
              چاپ رسید
            </button>

            <button
                v-if="canConfirmExit(detailsModal.item)"
                @click="openConfirmModal(detailsModal.item, 'confirm')"
                class="px-3 py-2 rounded-xl bg-emerald-600/15 border border-emerald-500/30 text-xs text-emerald-200 hover:bg-emerald-600/25 transition"
            >
              تأیید و کسر موجودی
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Action Modal -->
    <div
        v-if="actionModal.visible"
        class="fixed inset-0 z-[99995] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
        @click.self="closeActionModal"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-slate-950 shadow-2xl overflow-hidden">
        <header class="px-2.5 py-2 border-b border-white/10">
          <h4 class="text-sm font-bold text-white">
            {{ actionModalTitle }}
          </h4>
        </header>

        <div class="p-4">
          <div class="rounded-2xl border border-white/10 bg-white/[0.03] p-3">
            <p class="text-xs text-slate-300">
              درخواست خروج دستی
              <b class="text-cyan-300">#{{ actionModal.item.id }}</b>
            </p>

            <p class="text-[11px] text-slate-500 mt-2">
              جمع تعداد:
              <span class="text-emerald-300 font-bold" dir="ltr">
                {{ formatNumber(actionModal.item.total_quantity || calcExitTotal(actionModal.item)) }}
              </span>
            </p>
          </div>

          <p
              v-if="actionModal.type === 'confirm'"
              class="text-[11px] text-amber-300 mt-3 leading-6"
          >
            با تأیید پارتنر، موجودی محصولات این خروج دستی از انبار کم می‌شود و دیگر نباید به عنوان draft حساب شود.
          </p>

          <div class="grid grid-cols-2 gap-2 mt-5">
            <button
                @click="closeActionModal"
                :disabled="loading.action"
                class="px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-xs text-slate-200 disabled:opacity-50"
            >
              انصراف
            </button>

            <button
                @click="runAction"
                :disabled="loading.action"
                class="px-3 py-2 rounded-xl text-xs border disabled:opacity-50"
                :class="actionModal.type === 'confirm'
                  ? 'bg-emerald-600/20 border-emerald-500/30 text-emerald-200'
                  : 'bg-rose-600/20 border-rose-500/30 text-rose-200'"
            >
              <i v-if="loading.action" class="fas fa-circle-notch fa-spin ml-1"></i>
              {{ actionButtonText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
export default {
  name: 'StockExitSection',

  data() {
    return {
      error: null,
      notice: '',

      loading: {
        list: false,
        partners: false,
        action: false,
      },

      partners: [],

      filters: {
        search: '',
        partner: '',
        status: '',
        date_from: '',
        date_to: '',
      },

      exits: {
        items: [],
        total: 0,
        page: 1,
        page_size: 5,
        total_pages: 1,
      },

      summary: {
        pending: 0,
        confirmed: 0,
        cancelled: 0,
      },

      detailsModal: {
        visible: false,
        item: {},
      },

      actionModal: {
        visible: false,
        type: '',
        item: {},
      },
    }
  },
  watch: {
    partnerId() {
      this.exits.page = 1
      this.fetchExits()
    }
  },
  computed: {
    actionButtonText() {
      return this.actionModal.type === 'confirm'
          ? 'تأیید و کسر موجودی'
          : 'لغو درخواست'
    },

    actionModalTitle() {
      return this.actionModal.type === 'confirm'
          ? 'تأیید خروج دستی توسط پارتنر'
          : 'لغو درخواست خروج دستی'
    },

    isPartnerUser() {
      return this.user && this.user.user_type === 'prtnr'
    },

    isManagerUser() {
      return this.user && this.user.user_type === 'ma'
    },
  },
  created() {
    this.fetchPartners()
    this.fetchExits()
  },

  mounted() {
  },
  props: {
    title: {
      type: String,
      default: 'خروج دستی موجودی'
    },
    subtitle: {
      type: String,
      default: ' درخواست‌های خروج دستی؛ کسر موجودی فقط بعد از تأیید شریک انجام می‌شود'
    },
    partnerId: {
      type: [String, Number],
      default: null
    },
    limit: {
      type: Number,
      default: 5
    }
  },

  methods: {
    toast(msg) {
      this.notice = msg
      setTimeout(() => {
        this.notice = ''
      }, 3000)
    },

    cleanParams(obj) {
      const copy = { ...(obj || {}) }
      Object.keys(copy).forEach(k => {
        if (copy[k] === '' || copy[k] === null || copy[k] === undefined) {
          delete copy[k]
        }
      })
      return copy
    },

    apiGetPos(path, params) {
      return new Promise((resolve, reject) => {
        this.request({
          url: this.endpoint(`partner-stock/${path}`),
          method: 'get',
          params: this.cleanParams(params || {}),
          success: data => resolve(data),
          error: err => reject(err),
        })
      })
    },

    apiPostPos(path, data) {
      return new Promise((resolve, reject) => {
        this.request({
          url: this.endpoint(`partner-stock/${path}`),
          method: 'post',
          data: this.cleanParams(data || {}),
          success: data => resolve(data),
          error: err => reject(err),
        })
      })
    },

    async fetchPartners() {
      this.loading.partners = true

      try {
        const data = await this.apiGetPos('partnerList', {})
        this.partners = data.results || data || []
      } catch (e) {
        console.error('fetchPartners error:', e)
        this.partners = []
      } finally {
        this.loading.partners = false
      }
    },

    async fetchExits() {
      this.loading.list = true

      try {
        const params = {
          page: this.exits.page,
          page_size: this.exits.page_size,
          search: this.filters.search,
          partner: this.partnerId || this.filters.partner,
          status: this.filters.status,
          date_from: this.filters.date_from,
          date_to: this.filters.date_to,
        }

        const data = await this.apiGetPos('partner-stock-exits/', params)
        const results = data.results || data || []

        this.exits.items = results
        this.exits.total = data.count || results.length
        this.exits.total_pages = Math.max(
            1,
            Math.ceil(this.exits.total / this.exits.page_size)
        )

        this.calculateSummary(results)
      } catch (e) {
        console.error('fetchExits error:', e)
        this.toast('خطا در دریافت درخواست‌های خروج')
      } finally {
        this.loading.list = false
      }
    },

    calculateSummary(items) {
      const summary = {
            pending: 0,
            confirmed: 0,
            cancelled: 0,
          }

      ;(items || []).forEach(x => {
        if (this.isConfirmed(x)) summary.confirmed += 1
        else if (this.isCancelled(x)) summary.cancelled += 1
        else summary.pending += 1
      })

      this.summary = summary
    },

    refreshAll() {
      this.fetchPartners()
      this.fetchExits()
    },

    applyFilters() {
      this.exits.page = 1
      this.fetchExits()
    },

    clearFilters() {
      this.filters = {
        search: '',
        partner: '',
        status: '',
        date_from: '',
        date_to: '',
      }
      this.applyFilters()
    },

    goPage(page) {
      const next = Math.max(1, Math.min(this.exits.total_pages, page))
      this.exits.page = next
      this.fetchExits()
    },

    openDetails(x) {
      this.detailsModal = {
        visible: true,
        item: x || {},
      }
    },

    closeDetails() {
      this.detailsModal.visible = false
      this.detailsModal.item = {}
    },

    openConfirmModal(x, type) {
      this.actionModal = {
        visible: true,
        type,
        item: x || {},
      }
    },

    closeActionModal() {
      if (this.loading.action) return
      this.actionModal.visible = false
      this.actionModal.type = ''
      this.actionModal.item = {}
    },
    canConfirmExit(x) {
      return this.isPartnerUser && this.isDraft(x)
    },

    canCancelExit(x) {
      if (!this.isDraft(x)) return false

      return this.isPartnerUser || this.isManagerUser
    },

    canPrintExit(x) {
      return Boolean(x && x.id)
    },

    formatNumber(value) {
      if (value === null || value === undefined || value === '') return '۰'

      const number = Number(value)
      if (Number.isNaN(number)) return '۰'

      return new Intl.NumberFormat('fa-IR').format(number)
    },

    formatDate(value) {
      if (!value) return '-'

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'

      return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    },

    getApiErrorMessage(error, fallback) {
      if (!error) return fallback

      if (typeof error === 'string') return error

      if (error.response && error.response.data) {
        const data = error.response.data

        if (typeof data === 'string') return data
        if (data.detail) return data.detail
        if (data.error) return data.error

        const firstKey = Object.keys(data)[0]
        if (firstKey && Array.isArray(data[firstKey])) {
          return data[firstKey][0]
        }

        if (firstKey && data[firstKey]) {
          return String(data[firstKey])
        }
      }

      if (error.data) {
        if (error.data.detail) return error.data.detail
        if (error.data.error) return error.data.error
      }

      return fallback
    },

    escapeHtml(value) {
      return String(value === null || value === undefined ? '' : value)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
    },

    printExit(x) {
      if (!x || !x.id) return

      const url = this.endpoint(
          `partner-stock/partner-stock-exits/${x.id}/print/html/?token=${this.token}`
      )

      window.open(url, '_blank')
    },

    async runAction() {
      const x = this.actionModal.item
      const type = this.actionModal.type

      if (!x || !x.id || !type) return

      this.loading.action = true

      try {
        const path = type === 'confirm'
            ? `partner-stock-exits/${x.id}/confirm/`
            : `partner-stock-exits/${x.id}/cancel/`

        const updated = await this.apiPostPos(path, {})

        this.toast(type === 'confirm'
            ? 'درخواست خروج تأیید شد و موجودی کم شد'
            : 'درخواست خروج لغو شد'
        )

        this.actionModal.visible = false
        this.$emit('stock-exit-updated', updated)

        if (this.detailsModal.visible && this.detailsModal.item && this.detailsModal.item.id === x.id) {
          this.detailsModal.item = updated
        }

        await this.fetchExits()
      } catch (e) {
        console.error('runAction error:', e)

        this.toast(this.getApiErrorMessage(
            e,
            type === 'confirm'
                ? 'خطا در تأیید درخواست خروج'
                : 'خطا در لغو درخواست خروج'
        ))
      } finally {
        this.loading.action = false
      }
    },

    calcExitTotal(x) {
      return (x.items || []).reduce((sum, it) => {
        return sum + Number(it.quantity || 0)
      }, 0)
    },

    partnerDisplay(x) {
      return (
          x.partner_name ||
          x.partner_title ||
          x.partner_full_name ||
          x.partner_display_name ||
          x.partner ||
          '---'
      )
    },

    isDraft(x) {
      return x && ['draft', 'dr', 'pending'].includes(x.status)
    },

    isConfirmed(x) {
      return x && ['confirmed', 'cf'].includes(x.status)
    },

    isCancelled(x) {
      return x && ['cancelled', 'ca'].includes(x.status)
    },

    statusLabel(status) {
      const map = {
        draft: 'درانتظار',
        dr: 'درانتظار',
        pending: 'درانتظار',
        confirmed: 'نهایی',
        cf: 'نهایی',
        cancelled: 'لغو',
        ca: 'لغو',
      }

      return map[status] || status || 'نامشخص'
    },

    statusClass(status) {
      if (['confirmed', 'cf'].includes(status)) {
        return 'bg-emerald-500/10 border-emerald-400/40 text-emerald-300'
      }

      if (['cancelled', 'ca'].includes(status)) {
        return 'bg-rose-500/10 border-rose-400/40 text-rose-300'
      }

      return 'bg-amber-500/10 border-amber-400/40 text-amber-200'
    },

    statusTextClass(status) {
      if (['confirmed', 'cf'].includes(status)) return 'text-emerald-300'
      if (['cancelled', 'ca'].includes(status)) return 'text-rose-300'
      return 'text-amber-300'
    },
  },
}
</script>

<style scoped>
.settlement-section,
.settlement-section * {
  box-sizing: border-box;
}

.settlement-section {
  contain: layout paint;
}

.truncate {
  min-width: 0;
}

.min-w-0 {
  min-width: 0 !important;
}

button {
  max-width: 100%;
}

img {
  max-width: 100%;
}
</style>